import { Check, MapPin, NavArrowDown, NavArrowUp } from '@byway/icons';
import { Combobox } from '@headlessui/react';

import clsx from 'clsx';
import { isWithinInterval } from 'date-fns';
import { Field } from 'formik';
import Fuse from 'fuse.js';
import { uniqBy } from 'lodash-es';
import { useLocale, useTranslations } from 'next-intl';
import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Text } from 'ui';
import { parseDate } from 'utils';
import { TRPC_SETTINGS } from '~/constants/trpc';
import type { Locale } from '~/i18n/i18n-config';
import { api } from '~/libs/api';
import { isOriginInRegion } from '~/utils/originHelpers';

type useOriginsSearchProps = {
  query: string;
  tripId?: string;
};

function useOriginsSearch({ query, tripId }: useOriginsSearchProps) {
  const locale = useLocale() as Locale;
  const { isLoading, data } = api.cms.origins.useQuery(
    {
      tripId,
      locale
    },
    {
      ...TRPC_SETTINGS.QUERY_NO_REFETCH
    }
  );

  const fuse = useMemo(() => {
    if (!data || !data.length) return null;

    return new Fuse(data, {
      keys: ['locationName'],
      threshold: 0.3,
      isCaseSensitive: false
    });
  }, [data]);

  if (!fuse) return { isLoading, data: data ?? [] };

  if (!query) return { isLoading, data: data ?? [] };

  const results = fuse.search(query);

  return {
    isLoading,
    data: uniqBy(
      results.map((result) => result.item),
      'locationName'
    )
  };
}

type Origin = ReturnType<typeof useOriginsSearch>['data'][0];

const FIELD_NAME = 'closest_city';

type ClosestCityProps = {
  startDate?: string;
  tripId?: string;
  onClose?: () => void;
  onCitySelect?: (cityName: string) => void;
};

// Reusable city button component
type CityButtonProps = {
  origin: Origin;
  isBlocked: boolean;
  isSelected: boolean;
  isActive?: boolean;
  onSelect: (origin: Origin) => void;
};

const CityButton = ({ origin, isBlocked, isSelected, isActive = false, onSelect }: CityButtonProps) => {
  const t = useTranslations('journey');
  const tAria = useTranslations('aria');

  return (
    <div className={clsx('border-t border-neutral-300')}>
      <button
        type="button"
        className={clsx(
          'text-left hover:bg-primary-200 p-2 outline-none w-full',
          'focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-opacity-75',
          isBlocked && 'opacity-50 cursor-not-allowed',
          isActive && 'font-medium',
          isActive && 'bg-primary-200'
        )}
        disabled={isBlocked}
        onClick={() => {
          if (!isBlocked) {
            onSelect(origin);
          }
        }}
        aria-disabled={isBlocked}
        aria-label={isBlocked ? tAria('origin_unavailable', { city: origin.locationName }) : origin.locationName}
        aria-pressed={isActive}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (!isBlocked) {
              onSelect(origin);
            }
          }
        }}
      >
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-1">
            <MapPin decorative size="large" aria-hidden="true" />
            <div>
              <Text variant="body-1" className="font-figtree text-sm font-normal leading-[140%]">
                {origin.locationName}
              </Text>
              {isBlocked && <p className="text-xs text-neutral-600">{t('origin_unavailable_date')}</p>}
            </div>
          </div>
          {isSelected && <Check size="large" decorative className="text-primary-500" />}
        </div>
      </button>
    </div>
  );
};

// Reusable accordion section component
type AccordionSectionProps = {
  title: string;
  sectionKey: 'UK' | 'Europe';
  activeSection: 'UK' | 'Europe' | null;
  toggleSection: (section: 'UK' | 'Europe', e: React.MouseEvent) => void;
  cities: Origin[];
  startDate?: string;
  selectedCity: string | undefined;
  activeCity: string | undefined;
  onSelectCity: (origin: Origin) => void;
};

const AccordionSection = ({
  title,
  sectionKey,
  activeSection,
  toggleSection,
  cities,
  startDate,
  selectedCity,
  activeCity,
  onSelectCity
}: AccordionSectionProps) => {
  const t = useTranslations('journey');
  const tAria = useTranslations('aria');
  const isActive = activeSection === sectionKey;
  const isEmpty = cities.length === 0;

  return (
    <div className={sectionKey === 'UK' ? 'border-b border-neutral-300' : ''}>
      <button
        className={clsx(
          'p-4 text-left flex items-center justify-between w-full transition-colors sticky top-0 z-10',
          isActive ? 'bg-neutral-100' : 'bg-white hover:bg-neutral-100',
          isEmpty && 'opacity-50 cursor-not-allowed hover:bg-white'
        )}
        onClick={(e) => !isEmpty && toggleSection(sectionKey, e)}
        aria-expanded={isActive}
        aria-controls={`${sectionKey.toLowerCase()}-cities-panel`}
        type="button"
        disabled={isEmpty}
        aria-disabled={isEmpty}
      >
        <Text variant="body-1" className={clsx(isActive ? 'font-bold' : '', isEmpty && 'text-neutral-400')}>
          {title}
        </Text>
        {isEmpty ? (
          <NavArrowDown size="large" decorative className="text-neutral-400" />
        ) : isActive ? (
          <NavArrowUp size="large" decorative className="transform rotate-90" />
        ) : (
          <NavArrowDown size="large" decorative />
        )}
      </button>

      {isActive && !isEmpty && (
        <section id={`${sectionKey.toLowerCase()}-cities-panel`} aria-label={tAria('cities_list', { region: title })}>
          {cities.length > 0 ? (
            <div className="border-b border-neutral-300">
              {cities.map((origin) => {
                const isBlocked =
                  (startDate &&
                    origin.blockoutDates?.some((blockout) =>
                      isWithinInterval(parseDate(startDate), {
                        start: parseDate(blockout.startDate),
                        end: parseDate(blockout.endDate)
                      })
                    )) ||
                  false;

                return (
                  <CityButton
                    key={origin.id}
                    origin={origin}
                    isBlocked={isBlocked}
                    isSelected={selectedCity === origin.locationName}
                    isActive={activeCity === origin.locationName}
                    onSelect={onSelectCity}
                  />
                );
              })}
            </div>
          ) : (
            <div className="py-2 text-center" aria-live="polite">
              <Text variant="body-1">{t('no_cities_in', { region: title })}</Text>
            </div>
          )}
        </section>
      )}
    </div>
  );
};

const ClosestCity = ({ onClose, tripId, startDate, onCitySelect }: ClosestCityProps) => {
  const t = useTranslations('journey');
  const tCommon = useTranslations('common');
  const [query, _setQuery] = useState('');
  const [selected, setSelected] = useState<Origin | null>(null);
  const [activeSection, setActiveSection] = useState<'UK' | 'Europe' | null>(null);
  const [initialSelectedCity, setInitialSelectedCity] = useState<string | undefined>(undefined);
  const [activeCity, setActiveCity] = useState<string | undefined>(undefined);

  const { isLoading, data } = useOriginsSearch({ query, tripId });

  // Get current field value using a ref to avoid dependency issues
  const fieldValueRef = useRef<string | undefined>(undefined);

  // Move the useEffect outside the Field render prop
  useEffect(() => {
    const currentSelectedCity = fieldValueRef.current;
    if (currentSelectedCity && !initialSelectedCity) {
      setInitialSelectedCity(currentSelectedCity);
      setActiveCity(currentSelectedCity);
    }
  }, [fieldValueRef.current, initialSelectedCity]);

  const toggleSection = (section: 'UK' | 'Europe', e: React.MouseEvent) => {
    // Prevent event propagation to stop triggering other buttons
    e.preventDefault();
    e.stopPropagation();

    setActiveSection((prev) => (prev === section ? null : section));
  };

  // Group cities by region
  const ukCities = data
    .filter((origin) => isOriginInRegion(origin, 'UK'))
    .sort((a, b) => {
      // Put London first
      if (a.locationName === 'London') return -1;
      if (b.locationName === 'London') return 1;
      // Sort other cities alphabetically
      return a.locationName.localeCompare(b.locationName);
    });
  const europeCities = data
    .filter((origin) => isOriginInRegion(origin, 'EU'))
    .sort((a, b) => a.locationName.localeCompare(b.locationName));

  return (
    <>
      <Field name={FIELD_NAME}>
        {({ field, form: { setFieldValue } }) => {
          // Store current field value in ref
          fieldValueRef.current = field.value;

          const handleSelectCity = (origin: Origin) => {
            setFieldValue(field.name, origin.locationName);
            setSelected(origin);
            setActiveCity(origin.locationName);
            if (onCitySelect) {
              onCitySelect(origin.locationName);
            }
            onClose?.();
          };

          return (
            <Combobox
              value={selected}
              onChange={(v) => {
                if (!v) return;
                setFieldValue(field.name, v.locationName);
                setSelected(v);
                setActiveCity(v.locationName);
                if (onCitySelect) {
                  onCitySelect(v.locationName);
                }
                onClose?.();
              }}
            >
              <div className="w-full h-[300px] overflow-y-auto">
                <div className="flex flex-col w-full">
                  <AccordionSection
                    title={t('origin_uk')}
                    sectionKey="UK"
                    activeSection={activeSection}
                    toggleSection={toggleSection}
                    cities={ukCities}
                    startDate={startDate}
                    selectedCity={initialSelectedCity}
                    activeCity={activeCity}
                    onSelectCity={handleSelectCity}
                  />

                  <AccordionSection
                    title={t('origin_europe')}
                    sectionKey="Europe"
                    activeSection={activeSection}
                    toggleSection={toggleSection}
                    cities={europeCities}
                    startDate={startDate}
                    selectedCity={initialSelectedCity}
                    activeCity={activeCity}
                    onSelectCity={handleSelectCity}
                  />
                </div>
              </div>
            </Combobox>
          );
        }}
      </Field>
      {isLoading && !!query && (
        <div className="flex items-center justify-center h-16">
          <Text variant="emphasis-1">{tCommon('loading')}</Text>
        </div>
      )}
      {query && !data.length && (
        <div className="pt-3 mx-4 md:max-w-[186px]">
          <Text variant="button" className="text-center text-ba" color="text-primary-S3">
            {t('origin_not_found')}
          </Text>
          <Text className="text-center">{t('origin_not_found_help')}</Text>
        </div>
      )}
    </>
  );
};

export { ClosestCity };
